import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useViewSync } from "../lib/view-sync";
import { MultiSelect } from "../components/MultiSelect";
import { useDashboard } from "../pages/Dashboard";

type Tree = {
  groups: { no: string; name: string | null }[];
  nodes: { key: string; parent: string | null; name: string }[];
  granted: Record<string, string[]>;
};
/** A right: held or not. A folder: all, some or none of the rights under it held. */
type Status = "yes" | "no" | "all" | "some" | "none";

const GROUPS_KEY = "po.userRightGroups";
const readGroups = (): string[] | null => {
  try {
    return JSON.parse(localStorage.getItem(GROUPS_KEY) ?? "null") as string[] | null;
  } catch {
    return null;
  }
};
const writeGroups = (v: string[]) => {
  try {
    localStorage.setItem(GROUPS_KEY, JSON.stringify(v));
  } catch {
    // storage unavailable: the choice is not remembered
  }
};

/**
 * User Info → User Right (SPEC §8.12), laid out as Sertica's grid: the right tree down the side, a
 * column for each chosen user group. Opening it reads the rights from Sertica again (about 20 s).
 */
export function UserRightsTab() {
  const { search } = useDashboard();
  const qc = useQueryClient();
  const [chosen, setChosen] = useState<string[] | null>(readGroups);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [diffOnly, setDiffOnly] = useState(false);
  const sync = useViewSync({ kind: "user-rights" }, () => void qc.invalidateQueries({ queryKey: ["user-rights"] }));
  const groupsQ = useQuery({ queryKey: ["user-rights", ""], queryFn: () => api<Tree>("/user-rights"), staleTime: 60_000 });
  // First visit: Administrators, as Sertica opens.
  const groups = chosen ?? (groupsQ.data?.groups.some((g) => g.no === "Administrators") ? ["Administrators"] : []);
  const q = useQuery({ queryKey: ["user-rights", groups.join(",")], queryFn: () => api<Tree>(`/user-rights?groups=${encodeURIComponent(groups.join(","))}`), enabled: groups.length > 0, placeholderData: (p) => p });
  const choose = (v: string[]) => {
    writeGroups(v);
    setChosen(v);
  };

  const data = q.data ?? groupsQ.data;
  const tree = useMemo(() => {
    if (!data) return null;
    const children = new Map<string | null, string[]>();
    const known = new Set(data.nodes.map((n) => n.key));
    for (const n of data.nodes) {
      const p = n.parent && known.has(n.parent) ? n.parent : null;
      children.set(p, [...(children.get(p) ?? []), n.key]);
    }
    const name = new Map(data.nodes.map((n) => [n.key, n.name]));
    // Each group's status per node: a folder from the rights under it.
    const status = new Map<string, Status[]>();
    const walk = (key: string): Status[] => {
      const kids = (children.get(key) ?? []).map(walk);
      const own = kids.length
        ? groups.map((_, i) => {
            const leaves = kids.map((k) => k[i]!);
            const held = leaves.every((s) => s === "yes" || s === "all");
            const none = leaves.every((s) => s === "no" || s === "none");
            return held ? "all" : none ? "none" : "some";
          })
        : groups.map((g) => (data.granted[g]?.includes(key) ? "yes" : "no"));
      status.set(key, own as Status[]);
      return own as Status[];
    };
    (children.get(null) ?? []).forEach(walk);
    return { children, name, status };
  }, [data, groups.join(",")]);

  // The header search and "differences only" keep a node that matches, with its folders above it.
  const needle = (search.q ?? "").trim().toLowerCase();
  const filtering = !!needle || diffOnly;
  const shown = useMemo(() => {
    if (!tree || !filtering) return null;
    const keep = new Set<string>();
    const differs = (k: string) => new Set(tree.status.get(k)!.map((s) => (s === "yes" ? "all" : s === "no" ? "none" : s))).size > 1;
    const visit = (k: string, parentMatch: boolean): boolean => {
      const match = parentMatch || !needle || tree.name.get(k)!.toLowerCase().includes(needle);
      const kids = tree.children.get(k) ?? [];
      let any = false;
      for (const c of kids) any = visit(c, match && !!needle) || any;
      const self = match && (!diffOnly || (!kids.length && differs(k)));
      if (self || any) keep.add(k);
      return self || any;
    };
    (tree.children.get(null) ?? []).forEach((k) => visit(k, false));
    return keep;
  }, [tree, needle, diffOnly]);

  const toggle = (k: string) => setOpen((o) => (o.has(k) ? new Set([...o].filter((x) => x !== k)) : new Set([...o, k])));
  const expandAll = () => setOpen(new Set([...(tree?.children.keys() ?? [])].filter((k): k is string => !!k)));
  const groupName = new Map((groupsQ.data?.groups ?? []).map((g) => [g.no, g.name ?? g.no]));

  const rows = (parent: string | null, depth: number): JSX.Element[] =>
    (tree?.children.get(parent) ?? [])
      .filter((k) => !shown || shown.has(k))
      .map((k) => {
        const kids = (tree!.children.get(k) ?? []).length > 0;
        // A search or filter opens every folder it keeps.
        const isOpen = kids && (filtering || open.has(k));
        return (
          <Fragment key={k}>
            <tr className="border-b border-line/60 hover:bg-hover">
              <th scope="row" className="sticky left-0 z-[1] bg-card font-normal text-left p-0">
                <button
                  className="flex items-center gap-1.5 w-full text-left py-[5px] pr-3 disabled:cursor-default"
                  style={{ paddingLeft: 8 + depth * 18 }}
                  disabled={!kids || filtering}
                  aria-expanded={kids ? isOpen : undefined}
                  onClick={() => toggle(k)}
                  title={k}
                >
                  <span aria-hidden className="w-3 text-muted text-[11px]">
                    {kids ? (isOpen ? "−" : "+") : ""}
                  </span>
                  <span className={kids ? "font-medium text-ink" : "text-ink"}>{tree!.name.get(k)}</span>
                </button>
              </th>
              {tree!.status.get(k)!.map((s, i) => (
                <td key={groups[i]} className="text-center border-l border-line/60 py-[3px]">
                  <StatusIcon s={s} />
                </td>
              ))}
            </tr>
            {isOpen && rows(k, depth + 1)}
          </Fragment>
        );
      });

  return (
    <div className="space-y-3">
      <p className="text-[11.5px] text-muted">
        Sertica&apos;s user rights, as its User Right grid shows them: pick the user groups to compare. A folder shows ✓ when every right in it is held,
        ⚠ when only some are. Opening this page reads the rights from Sertica again (about 20 s).
      </p>
      <section className="border border-line bg-card" aria-label="User rights">
        <div className="toolbar flex flex-wrap items-center gap-2 px-3 py-1.5">
          <h3 className="text-[13px] font-medium mr-2">User rights</h3>
          <MultiSelect
            label={groups.length ? `User groups (${groups.length})` : "Choose user groups"}
            name="user groups"
            options={(groupsQ.data?.groups ?? []).map((g) => ({ value: g.no, label: g.name && g.name !== g.no ? `${g.no} · ${g.name}` : g.no }))}
            value={groups}
            onChange={(v) => choose(v.slice(0, 12))}
            searchable
          />
          <label className="flex items-center gap-1.5 text-[12.5px]" title={groups.length < 2 ? "Choose two or more groups" : undefined}>
            <input type="checkbox" checked={diffOnly} disabled={groups.length < 2} onChange={(e) => setDiffOnly(e.target.checked)} />
            Differences only
          </label>
          <span className="flex-1" />
          {sync.status === "syncing" && <span className="text-[12px] opacity-80">Reading from Sertica…</span>}
          {sync.status === "failed" && <span className="text-[12px]">Couldn&apos;t read from Sertica; showing the last copy.</span>}
          <button className="text-[12.5px] underline" onClick={expandAll} disabled={filtering}>
            Expand all
          </button>
          <button className="text-[12.5px] underline" onClick={() => setOpen(new Set())} disabled={filtering}>
            Collapse all
          </button>
        </div>
        {q.isError || groupsQ.isError ? (
          <p className="px-3 py-4 text-[var(--danger)]" role="alert">
            Couldn&apos;t load the user rights.
          </p>
        ) : !tree ? (
          <p className="px-3 py-4 text-[13px] text-muted">Loading…</p>
        ) : !tree.children.get(null)?.length ? (
          <p className="px-3 py-4 text-[13px] text-muted">
            {sync.status === "syncing" ? "Reading the user rights from Sertica…" : "The right tree has not been read from Sertica yet."}
          </p>
        ) : !groups.length ? (
          <p className="px-3 py-4 text-[13px] text-muted">Choose one or more user groups to see their rights.</p>
        ) : (
          <div className="overflow-auto max-h-[calc(100vh-230px)]">
            <table className="text-[12.5px] border-collapse min-w-full">
              <thead className="sticky top-0 z-[2] bg-card">
                <tr className="border-b border-line">
                  <th scope="col" className="sticky left-0 z-[3] bg-card text-left font-semibold px-3 py-2 min-w-[260px]">
                    User Right
                  </th>
                  {groups.map((g) => (
                    <th key={g} scope="col" className="font-semibold px-3 py-2 border-l border-line min-w-[130px] max-w-[180px]" title={g}>
                      {groupName.get(g) ?? g}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows(null, 0)}
                {shown && !shown.size && (
                  <tr>
                    <td colSpan={groups.length + 1} className="px-3 py-4 text-muted">
                      {diffOnly && !needle ? "The chosen groups hold the same rights." : "No right matches."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

const svg = { width: 18, height: 18, viewBox: "0 0 18 18", "aria-hidden": true } as const;
const OK = "var(--stage-received)";
const WARN = "var(--budget-warn)";
const NO = "var(--danger)";

/** Sertica's marks: a green tick or a red no-entry for a right; a folder with a tick, warning or no-entry. */
function StatusIcon({ s }: { s: Status }) {
  const label = { yes: "Granted", no: "Not granted", all: "All granted", some: "Some granted", none: "None granted" }[s];
  const mark =
    s === "yes" || s === "all" ? (
      <path d="M4.5 9.2l3 3 6-6.4" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    ) : null;
  if (s === "yes")
    return (
      <span role="img" aria-label={label} title={label} className="inline-flex">
        <svg {...svg}>
          <rect x="1" y="1" width="16" height="16" rx="2.5" fill={OK} />
          {mark}
        </svg>
      </span>
    );
  if (s === "no")
    return (
      <span role="img" aria-label={label} title={label} className="inline-flex">
        <svg {...svg}>
          <circle cx="9" cy="9" r="6.5" fill="none" stroke={NO} strokeWidth="2.2" />
          <path d="M4.6 13.4l8.8-8.8" stroke={NO} strokeWidth="2.2" />
        </svg>
      </span>
    );
  // A folder, with a badge: tick, warning or no-entry.
  return (
    <span role="img" aria-label={label} title={label} className="inline-flex">
      <svg width={22} height={18} viewBox="0 0 22 18" aria-hidden>
        <path d="M1.5 3.5a1 1 0 0 1 1-1h4.5l1.5 1.5h7a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1z" fill="none" stroke="var(--ink-2)" strokeWidth="1.5" />
        {s === "all" && (
          <g>
            <rect x="11" y="8" width="10" height="10" rx="2" fill={OK} />
            <path d="M13.2 13l2 2 3.6-3.8" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </g>
        )}
        {s === "some" && (
          <g>
            <path d="M16 7.5l5 9.5h-10z" fill={WARN} />
            <path d="M16 11v2.6M16 15.2v.3" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
          </g>
        )}
        {s === "none" && (
          <g>
            <circle cx="16" cy="13" r="4.2" fill="var(--card)" stroke={NO} strokeWidth="1.6" />
            <path d="M13.2 15.8l5.6-5.6" stroke={NO} strokeWidth="1.6" />
          </g>
        )}
      </svg>
    </span>
  );
}
